import { combineReducers } from 'redux';
import filter from 'lodash.filter';
import sortBy from 'lodash.sortby';
import prepareBody from '../utils/prepareBody';
import { selectPlanId, selectRunnerId } from './auth';
import {
    mileToKm,
    kmToMile,
    paceMileToMph,
} from '../utils/convertUnits';

export const FETCH_POSTS = 'hub/FETCH_POSTS';
export const FETCH_POSTS_SUCCESS = 'hub/FETCH_POSTS_SUCCESS';
export const FETCH_POSTS_FAILURE = 'hub/FETCH_POSTS_FAILURE';
export const FETCH_CONNECT = 'hub/FETCH_CONNECT';
export const FETCH_CONNECT_SUCCESS = 'hub/FETCH_CONNECT_SUCCESS';
export const FETCH_CONNECT_FAILURE = 'hub/FETCH_CONNECT_FAILURE';
export const CHANGE_CONVERTER_FIELD = 'hub/CHANGE_CONVERTER_FIELD';
export const RESET_CONVERTER = 'hub/RESET_CONVERTER';

export const education = (state = {
    nutrition: [],
    running: [],
    strength: [],
    glossary: [],
    faq: [],
    strategy: [],
    fundraising: [],
}, action) => {
    switch (action.type) {
        case FETCH_POSTS_SUCCESS:
            return {
                ...state,
                ...formatEducationResults(action.result),
            };
        default:
            return state;
    }
};

export const connectData = (state = {
    socialConnections: {},
    article1: {},
    socialFollow: {},
}, action) => {
    switch (action.type) {
        case FETCH_CONNECT_SUCCESS:
            return {
                ...state,
                ...formatConnectResults(action.result),
            };
        default:
            return state;
    }
};

export const loadingPosts = (state = true, action) => {
    switch (action.type) {
        case FETCH_POSTS:
            return true;
        case FETCH_POSTS_SUCCESS:
        case FETCH_POSTS_FAILURE:
            return false;
        default:
            return state;
    }
};

export const loadingConnect = (state = true, action) => {
    switch (action.type) {
        case FETCH_CONNECT:
            return true;
        case FETCH_CONNECT_SUCCESS:
        case FETCH_CONNECT_FAILURE:
            return false;
        default:
            return state;
    }
};

export const converterPace = (state = {}, action) => {
    switch (action.type) {
        case CHANGE_CONVERTER_FIELD: {
            const newState = {
                miMin: 0,
                miSec: 0,
                kmMin: 0,
                kmSec: 0,
                tmMph: 0,
                ...state,
                [action.key]: action.val,
            };
            const { miMin, miSec, kmMin, kmSec, tmMph } = newState;
            const miPaceSec = (miMin * 60) + miSec;
            const kmPaceSec = (kmMin * 60) + kmSec;

            // these functions are flipped because it is pace
            const kmPaceFromMi = Math.round(kmToMile(miPaceSec));
            const miPaceFromKm = Math.round(mileToKm(kmPaceSec));
            const miPaceFromMph = Math.round(paceMileToMph(tmMph));
            const kmPaceFromMph = Math.round(kmToMile(paceMileToMph(tmMph)));

            switch (action.convertFrom) {
                case 'mi':
                    return {
                        miMin,
                        miSec,
                        kmMin: Math.floor(kmPaceFromMi / 60),
                        kmSec: Math.round(kmPaceFromMi % 60),
                        tmMph: paceMileToMph(miPaceSec),
                    };
                case 'km':
                    return {
                        miMin: Math.floor(miPaceFromKm / 60),
                        miSec: Math.round(miPaceFromKm % 60),
                        kmMin,
                        kmSec,
                        tmMph: paceMileToMph(miPaceFromKm),
                    };
                case 'tm':
                    return {
                        miMin: Math.floor(miPaceFromMph / 60),
                        miSec: Math.round(miPaceFromMph % 60),
                        kmMin: Math.floor(kmPaceFromMph / 60),
                        kmSec: Math.round(kmPaceFromMph % 60),
                        tmMph,
                    };
                default:
                    return state;
            }
        }
        case RESET_CONVERTER:
            return {};
        default:
            return state;
    }
};

export default combineReducers({
    education,
    loadingPosts,
    loadingConnect,
    connectData,
    converterPace,
});

export const fetchPostsAction = () => ({
    types: [FETCH_POSTS, FETCH_POSTS_SUCCESS, FETCH_POSTS_FAILURE],
    promise: ({ getState }) =>
        fetch(`${process.env.RTAPI_URL}/API_education.php`, {
            method: 'POST',
            body: prepareBody({
                plan_id: selectPlanId(getState()),
                runner_id: selectRunnerId(getState()),
            }),
        }).then(res => res.json()),
});

export const fetchConnectDataAction = () => ({
    types: [FETCH_CONNECT, FETCH_CONNECT_SUCCESS, FETCH_CONNECT_FAILURE],
    promise: ({ getState }) =>
        fetch(`${process.env.RTAPI_URL}/API_connect.php`, {
            method: 'POST',
            body: prepareBody({
                plan_id: selectPlanId(getState()),
                runner_id: selectRunnerId(getState()),
            }),
        }).then(res => res.json()),
});

export const converterChange = (key, val, convertFrom) => ({
    type: CHANGE_CONVERTER_FIELD,
    key,
    val: Number(val),
    convertFrom,
});

export const converterReset = () => ({
    type: RESET_CONVERTER,
});

export const formatEducationResults = (results) => {
    const nutritionPosts = filter(results, post => post.Category === 'Nutrition');
    const runningPosts = filter(results, post => post.Category === 'Running');
    const strengthPosts = filter(results, post => post.Category === 'Strength');
    const glossaryPosts = filter(results, post => post.Category === 'Glossary');
    const faqPosts = filter(results, post => post.Category === 'FAQ');
    const strategyPosts = filter(results, post => post.Category === 'Strategy');
    const fundraisingPosts = filter(results, post => post.Category === 'Fundraise');
    return {
        nutrition: sortBy(nutritionPosts, post => +post.Category_display_order),
        running: sortBy(runningPosts, post => +post.Category_display_order),
        strength: sortBy(strengthPosts, post => +post.Category_display_order),
        glossary: sortBy(glossaryPosts, post => +post.Category_display_order),
        faq: sortBy(faqPosts, post => +post.Category_display_order),
        strategy: sortBy(strategyPosts, post => +post.Category_display_order),
        fundraising: sortBy(fundraisingPosts, post => +post.Category_display_order),
    };
};

export const formatConnectResults = (results) => {
     // I'm not sure why he's returning this as 'Article1' and why instead of objects
    // everything is inside arrays
    const socialConnections = {
        fb_group: results[0].length && results[0][1]['FB Group'],
        fb_group_text: results[0].length && results[0][1]['FB Group Text'],
        facebook: results[0].length && results[0][1].Facebook,
        instagram: results[0].length && results[0][1].Instagram,
        twitter: results[0].length && results[0][1].Twitter,
    };
    const article1 = {
        title: results[1].length && results[1][1].title,
        text: results[1].length && results[1][1].text,
        media: results[1].length && results[1][1].Media_url,
        externalUrl: results[1].length && results[1][1].External_link,
    };
    const socialFollow = {
        social_link: results[2].length && results[2][1].social_link,
        button_copy: results[2].length && results[2][1].button_copy,
    };
    return { socialConnections, article1, socialFollow };
};



/** WEBPACK FOOTER **
 ** ./src/redux/hub.js
 **/